<template>
    <div class="faq-page">
        <div class="faq-container">
            <h1 class="faq-eyebrow">PREGUNTAS FRECUENTES</h1>

            <h1 class="faq-title">
                Todo lo que necesitás saber sobre AgBI
            </h1>

            <!-- SOBRE LA PLATAFORMA -->
            <section class="faq-section">
                <h2 class="faq-section-title">
                    SOBRE LA PLATAFORMA
                </h2>

                <div
                    class="faq-item"
                    :class="{ 'is-open': isOpen(1) }"
                >
                    <button
                        type="button"
                        class="faq-question"
                        @click="toggle(1)"
                    >
                        <span>¿Qué es AgBI?</span>

                        <span class="faq-icon">
                            {{ isOpen(1) ? '−' : '+' }}
                        </span>
                    </button>

                    <div
                        v-show="isOpen(1)"
                        class="faq-answer"
                    >
                        <p>
                            Es nuestra plataforma colaborativa de Business Intelligence aplicada exclusivamente al sector agroindustrial.
                            AgBI transforma datos complejos en información visual, clara y simple, permitiéndote entender la estructura económica
                            de los negocios del sector de manera estratégica.
                        </p>
                    </div>
                </div>

                <div
                    class="faq-item"
                    :class="{ 'is-open': isOpen(2) }"
                >
                    <button
                        type="button"
                        class="faq-question"
                        @click="toggle(2)"
                    >
                        <span>¿Cuál es el objetivo principal de AgBI?</span>

                        <span class="faq-icon">
                            {{ isOpen(2) ? '−' : '+' }}
                        </span>
                    </button>

                    <div
                        v-show="isOpen(2)"
                        class="faq-answer"
                    >
                        <p>
                            Ser tu principal herramienta de soporte para la toma de decisiones. AgBI te permite monitorear las variables
                            que realmente impactan en la rentabilidad, comparar resultados entre distintos cultivos y entre las diferentes
                            zonas productivas, así como evaluar escenarios para mitigar riesgos.
                        </p>
                    </div>
                </div>

                <div
                    class="faq-item"
                    :class="{ 'is-open': isOpen(3) }"
                >
                    <button
                        type="button"
                        class="faq-question"
                        @click="toggle(3)"
                    >
                        <span>¿Cómo genera AgBI su información y resultados?</span>

                        <span class="faq-icon">
                            {{ isOpen(3) ? '−' : '+' }}
                        </span>
                    </button>

                    <div
                        v-show="isOpen(3)"
                        class="faq-answer"
                    >
                        <p>
                            Nuestra metodología combina precisión técnica y realidad de campo.
                            Estructuramos costos y resultados basándonos en:
                        </p>

                        <ul>
                            <li>
                                <strong>Planteos técnicos:</strong>
                                considerando 3 niveles tecnológicos y consensuados con especialistas zonales de Argentina.
                            </li>

                            <li>
                                <strong>Gestión de referencia:</strong>
                                Aplicación de normas de gestión profesional.
                            </li>

                            <li>
                                <strong>Mercado en tiempo real:</strong>
                                Precios de referencia de granos e insumos actualizados.
                            </li>

                            <li>
                                <strong>Proyecciones locales:</strong>
                                Rendimientos estimados según la realidad de cada región.
                            </li>
                        </ul>
                    </div>
                </div>
            </section>

            <!-- DATOS Y ACTUALIZACION -->
            <section class="faq-section">
                <h2 class="faq-section-title">
                    DATOS Y ACTUALIZACIÓN
                </h2>

                <div
                    class="faq-item"
                    :class="{ 'is-open': isOpen(4) }"
                >
                    <button
                        type="button"
                        class="faq-question"
                        @click="toggle(4)"
                    >
                        <span>¿Con qué frecuencia se actualizan los datos?</span>

                        <span class="faq-icon">
                            {{ isOpen(4) ? '−' : '+' }}
                        </span>
                    </button>

                    <div
                        v-show="isOpen(4)"
                        class="faq-answer"
                    >
                        <p>
                            La actualización es dinámica y adaptada al mercado:
                        </p>

                        <ul>
                            <li>
                                <strong>Indicadores de mercado:</strong>
                                Actualización diaria.
                            </li>

                            <li>
                                <strong>Márgenes y reportes estratégicos:</strong>
                                Periodicidad quincenal o mensual, respetando el ciclo biológico de los cultivos
                                y la volatilidad del mercado.
                            </li>
                        </ul>
                    </div>
                </div>

                <div
                    class="faq-item"
                    :class="{ 'is-open': isOpen(5) }"
                >
                    <button
                        type="button"
                        class="faq-question"
                        @click="toggle(5)"
                    >
                        <span>¿De dónde provienen las fuentes de información?</span>

                        <span class="faq-icon">
                            {{ isOpen(5) ? '−' : '+' }}
                        </span>
                    </button>

                    <div
                        v-show="isOpen(5)"
                        class="faq-answer"
                    >
                        <p>
                            Este es nuestro mayor diferencial: utilizamos nuestra propia red de datos primarios relevados por los consultores
                            de AZ Group directamente a campo. Esta base se complementa y valida con fuentes oficiales como la Secretaría
                            de Agricultura, Bolsas de Comercio, mercados de futuros A3 (ex Matba Rofex) y los valores relevados de mercado.
                        </p>
                    </div>
                </div>
            </section>

            <!-- FUNCIONALIDADES -->
            <section class="faq-section">
                <h2 class="faq-section-title">
                    FUNCIONALIDADES Y MÓDULOS
                </h2>

                <div
                    class="faq-item"
                    :class="{ 'is-open': isOpen(6) }"
                >
                    <button
                        type="button"
                        class="faq-question"
                        @click="toggle(6)"
                    >
                        <span>¿Qué encuentro en la sección "Regiones"?</span>

                        <span class="faq-icon">
                            {{ isOpen(6) ? '−' : '+' }}
                        </span>
                    </button>

                    <div
                        v-show="isOpen(6)"
                        class="faq-answer"
                    >
                        <p>
                            Un resumen ejecutivo de los principales indicadores económicos y productivos segmentados geográficamente,
                            permitiéndote entender el pulso de cada zona de influencia.
                        </p>
                    </div>
                </div>

                <div
                    class="faq-item"
                    :class="{ 'is-open': isOpen(7) }"
                >
                    <button
                        type="button"
                        class="faq-question"
                        @click="toggle(7)"
                    >
                        <span>¿Qué tipo de datos se detallan en la sección "Cultivos"?</span>

                        <span class="faq-icon">
                            {{ isOpen(7) ? '−' : '+' }}
                        </span>
                    </button>

                    <div
                        v-show="isOpen(7)"
                        class="faq-answer"
                    >
                        <p>
                            Encontrarás la "radiografía" de cada cultivo: indicadores específicos, márgenes detallados y costos desglosados,
                            siempre segmentados por región para una mayor precisión.
                        </p>
                    </div>
                </div>

                <div
                    class="faq-item"
                    :class="{ 'is-open': isOpen(8) }"
                >
                    <button
                        type="button"
                        class="faq-question"
                        @click="toggle(8)"
                    >
                        <span>¿Puedo comparar diferentes negocios o zonas?</span>

                        <span class="faq-icon">
                            {{ isOpen(8) ? '−' : '+' }}
                        </span>
                    </button>

                    <div
                        v-show="isOpen(8)"
                        class="faq-answer"
                    >
                        <p>
                            Sí. El módulo "Comparativa" permite contrastar indicadores entre distintos cultivos y regiones.
                            Además, es interactivo: podés editar parámetros específicos para evaluar escenarios propios
                            y ver cómo impactan en tus resultados.
                        </p>
                    </div>
                </div>

                <div
                    class="faq-item"
                    :class="{ 'is-open': isOpen(9) }"
                >
                    <button
                        type="button"
                        class="faq-question"
                        @click="toggle(9)"
                    >
                        <span>¿Qué se puede ver en la sección de precios?</span>

                        <span class="faq-icon">
                            {{ isOpen(9) ? '−' : '+' }}
                        </span>
                    </button>

                    <div
                        v-show="isOpen(9)"
                        class="faq-answer"
                    >
                        <p>
                            AgBI te brinda una visión 360° de los precios del mercado y aporta distintos análisis sobre los mismos:
                        </p>

                        <ul>
                            <li>
                                Cierres diarios y evolución de precios disponibles y futuros de A3 (ex Matba Rofex).
                            </li>

                            <li>
                                <strong>Herramientas de cobertura:</strong>
                                Visualización de opciones de A3 (Calls y Puts).
                            </li>

                            <li>
                                <strong>Análisis avanzado:</strong>
                                Estacionalidad, pases y relaciones de precios para identificar oportunidades de venta.
                            </li>
                        </ul>
                    </div>
                </div>

                <div
                    class="faq-item"
                    :class="{ 'is-open': isOpen(10) }"
                >
                    <button
                        type="button"
                        class="faq-question"
                        @click="toggle(10)"
                    >
                        <span>¿Qué ofrece la nueva sección de "Ganadería"?</span>

                        <span class="faq-icon">
                            {{ isOpen(10) ? '−' : '+' }}
                        </span>
                    </button>

                    <div
                        v-show="isOpen(10)"
                        class="faq-answer"
                    >
                        <p>
                            Contamos con simuladores dinámicos para el negocio de Engorde a Corral y de Recría.
                            Estos tableros permiten:
                        </p>

                        <ul>
                            <li>
                                Ver el margen del negocio con parámetros de referencia.
                            </li>

                            <li>
                                <strong>Simular en vivo:</strong>
                                Editando parámetros de ingresos y costos, se podrá ver el impacto inmediato en el resultado,
                                permitiéndote proyectar tu propio negocio ganadero.
                            </li>
                        </ul>
                    </div>
                </div>
            </section>

            <!-- CONTACTO -->
            <section class="faq-section">
                <h2 class="faq-section-title">
                    CONTACTO Y SOPORTE
                </h2>

                <div
                    class="faq-item"
                    :class="{ 'is-open': isOpen(11) }"
                >
                    <button
                        type="button"
                        class="faq-question"
                        @click="toggle(11)"
                    >
                        <span>¿Cómo puedo ponerme en contacto con el equipo de AgBI?</span>

                        <span class="faq-icon">
                            {{ isOpen(11) ? '−' : '+' }}
                        </span>
                    </button>

                    <div
                        v-show="isOpen(11)"
                        class="faq-answer"
                    >
                        <p>
                            Estamos para ayudarte. Podés escribirnos a través de nuestro formulario de contacto en la página principal
                            de la plataforma. Nuestro equipo de soporte técnico y analistas te responderá a la brevedad para asegurar
                            que aproveches al máximo la herramienta.
                        </p>
                    </div>
                </div>
            </section>
        </div>
    </div>
</template>

<script>
export default {
    name: 'Questions',

    data() {
        return {
            openQuestion: 1
        };
    },

    methods: {
        toggle(question) {
            this.openQuestion =
                this.openQuestion === question
                    ? null
                    : question;
        },

        isOpen(question) {
            return this.openQuestion === question;
        }
    }
};
</script>

<style>
.faq-page {
    min-height: 100%;
    padding: 36px 0 64px;
    background: #ffffff;
    font-family: 'Gotham-Book', Arial, Helvetica, sans-serif;
}

.faq-container {
    width: calc(100% - 96px);
    max-width: 1380px;
    margin: 0 auto;
}

.faq-eyebrow {
    margin-bottom: 15px;
    font-family: 'Gotham-Book', Arial, Helvetica, sans-serif;
    color: #0061ad;

    font-size: 12px;
    font-weight: 700;

    letter-spacing: 1.5px;
}

.faq-title {
    margin: 0 0 62px;

    color: #000000;

    font-size: 1.75rem;
    line-height: 1.14;
    font-weight: 900;

    letter-spacing: -0.5px;
}

.faq-section {
    margin-bottom: 42px;
}

.faq-section-title {
    margin: 0 0 18px;

    color: #0061ad;
    font-family: 'Gotham-Book', Arial, Helvetica, sans-serif;
    font-size: 14px;
    line-height: 1.3;
    font-weight: 600;

    letter-spacing: 1.5px;
}

.faq-item {
    margin-bottom: 12px;

    overflow: hidden;

    background: #f5f8fb;

    border: 1px solid #dce5ed;
    border-radius: 15px;

    transition:
        border-color 0.2s ease,
        box-shadow 0.2s ease;
}

.faq-item:hover {
    border-color: #cbd8e3;
}

.faq-question {
    width: 100%;
    min-height: 66px;

    padding: 20px 24px;

    display: flex;
    align-items: center;
    justify-content: space-between;

    gap: 24px;

    border: 0;
    outline: 0;

    background: transparent;

    color: #000;

    font-family: 'Gotham-Book', Arial, Helvetica, sans-serif;
    font-size: 14px;
    font-weight: 900;
    line-height: 1.35;

    text-align: left;

    cursor: pointer;
}

.faq-question:focus {
    outline: none;
}

.faq-icon {
    flex: 0 0 24px;

    color: #0061ad;

    font-size: 25px;
    font-weight: 900;
    line-height: 1;

    text-align: center;
}

.faq-answer {
    padding: 0 24px 22px;

    color: #000;

    font-size: 14px;
    line-height: 1.7;
}

.faq-answer p {
    margin: 0;
}

.faq-answer ul {
    margin: 14px 0 0;
    padding-left: 22px;
}

.faq-answer li {
    margin-bottom: 8px;
}

.faq-answer li:last-child {
    margin-bottom: 0;
}

.faq-answer strong {
    color: #000;
    font-weight: 900;
}


/* TABLET */

@media only screen and (max-width: 960px) {
    .faq-container {
        width: calc(100% - 48px);
    }

    .faq-title {
        margin-bottom: 48px;
        font-size: 36px;
    }
}


/* MOBILE */

@media only screen and (max-width: 600px) {
    .faq-page {
        padding: 28px 0 44px;
    }

    .faq-container {
        width: calc(100% - 28px);
    }

    .faq-eyebrow {
        margin-bottom: 16px;
        font-size: 13px;
    }

    .faq-title {
        margin-bottom: 38px;

        font-size: 30px;
        line-height: 1.18;
    }

    .faq-section {
        margin-bottom: 34px;
    }

    .faq-section-title {
        font-size: 14px;
    }

    .faq-item {
        border-radius: 12px;
    }

    .faq-question {
        min-height: 60px;

        padding: 17px 18px;

        gap: 16px;

        font-size: 16px;
    }

    .faq-answer {
        padding: 0 18px 19px;

        font-size: 15px;
        line-height: 1.6;
    }
}
</style>